import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	TPpwrCountryExclusion,
	TPpwrCountryExclusionBody,
	TPpwrCountryExclusionsResponsePayload,
	TPpwrDownloadProofDocumentRequest,
	TPpwrManualReportingRequestPayload,
	TPpwrManualReportingResponsePayload,
	TPpwrManualReportRequest,
	TPpwrPackingTypeEnabledRequest,
	TPpwrPackingTypesResponsePayload,
	TPpwrPackingTypeWeightRequest,
	TPpwrStore,
	TPpwrUpdateCountryExclusionRequest,
	TPpwrUploadProofDocumentRequest,
	TPpwrUploadProofDocumentSuccess,
} from './types';

export const initialState: TPpwrStore = {
	packingTypes: null,
	packingTypesLoading: false,
	updatePackingTypeWeightLoading: false,
	updatePackingTypeEnabledLoading: false,
	uploadProofDocumentLoading: false,
	deleteProofDocumentLoading: false,
	downloadProofDocumentLoading: false,
	countryExclusions: null,
	countryExclusionsLoading: false,
	saveCountryExclusionLoading: false,
	manualReporting: null,
	manualReportingLoading: false,
	upsertManualReportLoading: false,
};

const ppwr = createSlice({
	name: '@@ppwr',
	initialState,
	reducers: {
		getPpwrPackingTypesRequest: (state: TPpwrStore) => {
			const nextState = state;
			nextState.packingTypesLoading = true;
		},
		getPpwrPackingTypesSuccess: (
			state: TPpwrStore,
			action: PayloadAction<TPpwrPackingTypesResponsePayload>,
		) => {
			const nextState = state;
			nextState.packingTypes = action.payload;
			nextState.packingTypesLoading = false;
		},
		updatePpwrPackingTypeWeightRequest: (
			state: TPpwrStore,
			action: PayloadAction<TPpwrPackingTypeWeightRequest>,
		) => {
			const nextState = state;
			nextState.updatePackingTypeWeightLoading = true;
		},
		updatePpwrPackingTypeWeightSuccess: (
			state: TPpwrStore,
			action: PayloadAction<TPpwrPackingTypeWeightRequest | null>,
		) => {
			const nextState = state;
			const packingType = nextState.packingTypes?.find(
				(item) => item.id === action.payload?.packingTypeId,
			);
			if (packingType && action.payload) {
				packingType.weight = action.payload.weight;
				packingType.weightProductPack = action.payload.weightProductPack;
			}
			nextState.updatePackingTypeWeightLoading = false;
		},
		updatePpwrPackingTypeEnabledRequest: (
			state: TPpwrStore,
			action: PayloadAction<TPpwrPackingTypeEnabledRequest>,
		) => {
			const nextState = state;
			nextState.updatePackingTypeEnabledLoading = true;
		},
		updatePpwrPackingTypeEnabledSuccess: (
			state: TPpwrStore,
			action: PayloadAction<TPpwrPackingTypeEnabledRequest | null>,
		) => {
			const nextState = state;
			const packingType = nextState.packingTypes?.find(
				(item) => item.id === action.payload?.packingTypeId,
			);
			if (packingType && action.payload) packingType.enabled = action.payload.enabled;
			nextState.updatePackingTypeEnabledLoading = false;
		},
		uploadPpwrProofDocumentRequest: (
			state: TPpwrStore,
			action: PayloadAction<TPpwrUploadProofDocumentRequest>,
		) => {
			const nextState = state;
			nextState.uploadProofDocumentLoading = true;
		},
		uploadPpwrProofDocumentSuccess: (
			state: TPpwrStore,
			action: PayloadAction<TPpwrUploadProofDocumentSuccess | null>,
		) => {
			const nextState = state;
			const packingType = nextState.packingTypes?.find(
				(item) => item.id === action.payload?.packingTypeId,
			);
			if (packingType && action.payload)
				packingType.documents = [...(packingType.documents ?? []), action.payload.document];
			nextState.uploadProofDocumentLoading = false;
		},
		deletePpwrProofDocumentRequest: (state: TPpwrStore, action: PayloadAction<number>) => {
			const nextState = state;
			nextState.deleteProofDocumentLoading = true;
		},
		deletePpwrProofDocumentSuccess: (state: TPpwrStore, action: PayloadAction<number | null>) => {
			const nextState = state;
			if (action.payload !== null)
				nextState.packingTypes?.forEach((item) => {
					const packingType = item;
					packingType.documents =
						packingType.documents?.filter((doc) => doc.id !== action.payload) ?? null;
				});
			nextState.deleteProofDocumentLoading = false;
		},
		downloadPpwrProofDocumentRequest: (
			state: TPpwrStore,
			action: PayloadAction<TPpwrDownloadProofDocumentRequest>,
		) => {
			const nextState = state;
			nextState.downloadProofDocumentLoading = true;
		},
		downloadPpwrProofDocumentSuccess: (state: TPpwrStore) => {
			const nextState = state;
			nextState.downloadProofDocumentLoading = false;
		},
		getPpwrCountryExclusionsRequest: (state: TPpwrStore) => {
			const nextState = state;
			nextState.countryExclusionsLoading = true;
		},
		getPpwrCountryExclusionsSuccess: (
			state: TPpwrStore,
			action: PayloadAction<TPpwrCountryExclusionsResponsePayload>,
		) => {
			const nextState = state;
			nextState.countryExclusions = action.payload;
			nextState.countryExclusionsLoading = false;
		},
		createPpwrCountryExclusionRequest: (
			state: TPpwrStore,
			action: PayloadAction<TPpwrCountryExclusionBody>,
		) => {
			const nextState = state;
			nextState.saveCountryExclusionLoading = true;
		},
		updatePpwrCountryExclusionRequest: (
			state: TPpwrStore,
			action: PayloadAction<TPpwrUpdateCountryExclusionRequest>,
		) => {
			const nextState = state;
			nextState.saveCountryExclusionLoading = true;
		},
		savePpwrCountryExclusionSuccess: (
			state: TPpwrStore,
			action: PayloadAction<TPpwrCountryExclusion | null>,
		) => {
			const nextState = state;
			if (action.payload) {
				const exclusion = action.payload;
				const list = nextState.countryExclusions ?? [];
				const index = list.findIndex((item) => item.id === exclusion.id);
				nextState.countryExclusions =
					index === -1
						? [...list, exclusion]
						: list.map((item) => (item.id === exclusion.id ? exclusion : item));
			}
			nextState.saveCountryExclusionLoading = false;
		},
		getPpwrManualReportingRequest: (
			state: TPpwrStore,
			action: PayloadAction<TPpwrManualReportingRequestPayload>,
		) => {
			const nextState = state;
			nextState.manualReportingLoading = true;
		},
		getPpwrManualReportingSuccess: (
			state: TPpwrStore,
			action: PayloadAction<TPpwrManualReportingResponsePayload>,
		) => {
			const nextState = state;
			nextState.manualReporting = action.payload;
			nextState.manualReportingLoading = false;
		},
		upsertPpwrManualReportRequest: (
			state: TPpwrStore,
			action: PayloadAction<TPpwrManualReportRequest>,
		) => {
			const nextState = state;
			nextState.upsertManualReportLoading = true;
		},
		upsertPpwrManualReportSuccess: (
			state: TPpwrStore,
			action: PayloadAction<TPpwrManualReportRequest | null>,
		) => {
			const nextState = state;
			const country = nextState.manualReporting?.data?.find(
				(item) => item.countryCode === action.payload?.countryCode,
			);
			if (country && action.payload) country.parcelsCount = action.payload.parcelsCount;
			nextState.upsertManualReportLoading = false;
		},
		ppwrInitState: () => initialState,
	},
});

export default ppwr.reducer;
export const {
	getPpwrPackingTypesRequest,
	getPpwrPackingTypesSuccess,
	updatePpwrPackingTypeWeightRequest,
	updatePpwrPackingTypeWeightSuccess,
	updatePpwrPackingTypeEnabledRequest,
	updatePpwrPackingTypeEnabledSuccess,
	uploadPpwrProofDocumentRequest,
	uploadPpwrProofDocumentSuccess,
	deletePpwrProofDocumentRequest,
	deletePpwrProofDocumentSuccess,
	downloadPpwrProofDocumentRequest,
	downloadPpwrProofDocumentSuccess,
	getPpwrCountryExclusionsRequest,
	getPpwrCountryExclusionsSuccess,
	createPpwrCountryExclusionRequest,
	updatePpwrCountryExclusionRequest,
	savePpwrCountryExclusionSuccess,
	getPpwrManualReportingRequest,
	getPpwrManualReportingSuccess,
	upsertPpwrManualReportRequest,
	upsertPpwrManualReportSuccess,
	ppwrInitState,
} = ppwr.actions;
